import React, {
  useCallback, useState, useMemo, useEffect,
} from 'react';
import * as Styles from './ModalOperationalSystems.styles';
import { useSelector, useDispatch } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import { useParams } from 'react-router-dom';
import { SelectField } from 'gatorcomponents';
import VpsStoreModal from '@/pages/Products/VpsOciDetailPage/ProductTemplatesPage/commons/VpsStoreModal';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils';
import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';
import { formatAddonsList } from '@/utils/Formatters/formatAddonsList';
import { billingAnalytics } from '@/analytics';

const ModalOperationalSystems = ({
  onClose,
  selectedItem,
}) => {
  const dispatch = useDispatch();

  const [modalType, setModalType] = useState(null);
  const [previousModalType, setPreviousModalType] = useState(null);
  const [selectedVersionId, setSelectedVersionId] = useState(null);
  const [bodyData, setBodyData] = useState({});
  const [requestChangeImage, setRequestChangeImage] = useState(false);

  const vpsOci = useSelector(state => state.vpsOci);
  const { availableOptions, addons, productName } = (vpsOci && vpsOci.data) || {};
  const { operationalSystems } = availableOptions || {};

  const changeImageLoading = useSelector(state => state.vpsOci?.changeImage?.loading);

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: localeVpsOci } = productDetailsLocale;
  const { operationalSystems: localeVpsOciStoreOperationalSystems } = localeVpsOci.store && localeVpsOci.store.changeApplications && localeVpsOci.store.changeApplications.modal;
  const { operationalSystems: localeVpsOciStoreDetailsOperationalSystems } = localeVpsOci.store && localeVpsOci.store.details;
  const { alert: localeVpsOciStoreModalAlert } = localeVpsOci && localeVpsOci.store && localeVpsOci.store.modal;

  const { hostingAndServers: hostingAndServersAnalytics } = billingAnalytics;

  const addonsPaidList = useMemo(() => (addons && addons.filter(addon => addon.isPaid)) || [], [addons]);
  const listAddonsNames = useMemo(() => (
    addonsPaidList && addonsPaidList.map(addon => addon.name)
  ), [addonsPaidList]);
  const formattedAddonsText = useMemo(() => formatAddonsList(listAddonsNames), [listAddonsNames]);
  const hasPaidAddons = useMemo(() => (addonsPaidList && addonsPaidList.length > 0), [addonsPaidList]);

  const selectedOperationalSystem = useMemo(() => operationalSystems && operationalSystems.find(so => selectedItem && so.slug === selectedItem),
    [operationalSystems, selectedItem]);

  const versions = selectedOperationalSystem && selectedOperationalSystem.versions;
  const hasMultipleVersions = versions && versions.length >= 2;

  const initialModalType = useMemo(() => (
    hasMultipleVersions ? 'withSelectionOfVersion' : 'withoutSelectionOfVersion'
  ), [hasMultipleVersions]);

  useEffect(() => {
    setModalType(initialModalType);
  }, [initialModalType]);

  const {
    name: operationalSystemName = '',
    slug: operationalSystemSlug = null,
  } = selectedOperationalSystem || {};

  const { id: hostingId } = useParams();

  const getDescriptionBySlug = useCallback((slug) => {
    const item = localeVpsOciStoreDetailsOperationalSystems[slug];
    return item ? item.description : null;
  }, [localeVpsOciStoreDetailsOperationalSystems]);

  const handleCloseModal = useCallback(() => {
    onClose && onClose();
    setBodyData(null);

    if (!requestChangeImage) {
      hostingAndServersAnalytics.hostingOsSelectVersionCancelClick(selectedItem, productName);
    }
  }, [onClose, hostingAndServersAnalytics, selectedItem, productName, requestChangeImage]);

  const handleChangeVersion = useCallback((selectedOption) => {
    setSelectedVersionId(selectedOption.value);
  }, []);

  const handleBackStep = useCallback(() => {
    setModalType(previousModalType);
    setBodyData(null);

    if (modalType === 'confirmWithPanelPaidActive') {
      hostingAndServersAnalytics.hostingOsPaymentBackClick(selectedItem, productName);
    } else if (modalType === 'confirmWithoutPanelPaidActive') {
      hostingAndServersAnalytics.hostingOsInstallBackClick(selectedItem, productName);
    }
  }, [previousModalType, hostingAndServersAnalytics, selectedItem, productName, modalType]);

  const handleStepVersion = useCallback(() => {
    let newImageId = null;

    if (hasMultipleVersions) {
      newImageId = selectedVersionId;
    } else {
      const defaultVersion = versions && versions[0];
      newImageId = defaultVersion ? defaultVersion.id : null;
    }

    setBodyData({
      hostingId,
      newImageId,
      imageType: 'os',
    });

    setPreviousModalType(modalType);

    hostingAndServersAnalytics.hostingOsSelectVersionContinueClick(selectedItem, productName);

    if (hasPaidAddons) {
      setModalType('confirmWithPanelPaidActive');
      return;
    }
    setModalType('confirmWithoutPanelPaidActive');
  }, [modalType, selectedVersionId, hasMultipleVersions, versions, hasPaidAddons, hostingId, hostingAndServersAnalytics, selectedItem, productName]);

  const handleStepConfirm = useCallback(() => {
    if (hostingId) {
      setRequestChangeImage(true);
      dispatch(vpsOciActions.changeImage.request(bodyData));

      hostingAndServersAnalytics.hostingOsInstallConfirmClick(selectedItem, productName);
    }
  }, [bodyData, dispatch, hostingId, hostingAndServersAnalytics, selectedItem, productName]);

  useEffect(() => {
    if (requestChangeImage && !changeImageLoading) {
      handleCloseModal();
      setRequestChangeImage(false);
    }
  }, [changeImageLoading, handleCloseModal, requestChangeImage]);

  const modalOperationalSystemDetails = useMemo(() => {
    const icon = operationalSystemSlug ? getIconBySlug(operationalSystemSlug) : null;
    const description = operationalSystemSlug ? getDescriptionBySlug(operationalSystemSlug) : null;

    const selectedSOInfoData = {
      name: operationalSystemName,
      description: description || '',
      icon: icon ? (
        <Styles.SelectedSOTitleIcon
          src={icon}
          alt={operationalSystemName}
          data-testid={`vpsoci-templates-selectedSo-icon-${operationalSystemSlug}`}
        />
      ) : null,
    };

    const optionsList = versions && versions.map(item => ({
      label: `${operationalSystemName} ${item.version}`,
      value: item.id,
    }));

    if (!selectedSOInfoData) return null;

    return (
      <Styles.SelectedSO data-testid="vpsoci-templates-selectedSo">
        <Styles.SelectedSOTitle data-testid="vpsoci-templates-selectedSo-name">
          {selectedSOInfoData.icon}
          {selectedSOInfoData.name}
          {' '}
          {hasMultipleVersions ? '' : (versions && versions[0] && versions[0].version)}
        </Styles.SelectedSOTitle>
        <Styles.SelectedSODescription data-testid="vpsoci-templates-selectedSo-description">
          {selectedSOInfoData.description}
        </Styles.SelectedSODescription>

        {hasMultipleVersions && (
          <Styles.SelectWrapper>
            <SelectField
              placeholder={localeVpsOciStoreOperationalSystems.placeholder.versions}
              options={optionsList}
              onClickSelect={option => handleChangeVersion(option)}
              withScroll
              filterable
            />
          </Styles.SelectWrapper>
        )}
      </Styles.SelectedSO>
    );
  }, [
    versions,
    hasMultipleVersions,
    getDescriptionBySlug,
    localeVpsOciStoreOperationalSystems.placeholder.versions,
    handleChangeVersion,
    operationalSystemSlug,
    operationalSystemName,
  ]);

  const modalConfig = useMemo(() => ({
    withoutSelectionOfVersion: {
      title: localeVpsOciStoreOperationalSystems.title,
      message: modalOperationalSystemDetails,
      alert: null,
      testId: 'vpsoci-operationalSystems-withoutSelectionOfVersion-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreOperationalSystems.withoutSelectionOfVersion.confirmButton,
        action: handleStepVersion,
        testId: 'vpsoci-operationalSystems-withoutSelectionOfVersion-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreOperationalSystems.withoutSelectionOfVersion.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-operationalSystems-withoutSelectionOfVersion-confirm-modal-close-button',
      },
    },
    withSelectionOfVersion: {
      title: localeVpsOciStoreOperationalSystems.title,
      message: modalOperationalSystemDetails,
      alert: null,
      testId: 'vpsoci-operationalSystems-withSelectionOfVersion-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreOperationalSystems.withSelectionOfVersion.confirmButton,
        action: handleStepVersion,
        disabled: !selectedVersionId,
        testId: 'vpsoci-operationalSystems-withSelectionOfVersion-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreOperationalSystems.withSelectionOfVersion.closeButton,
        action: handleCloseModal,
        testId: 'vpsoci-operationalSystems-withSelectionOfVersion-confirm-modal-close-button',
      },
    },
    confirmWithoutPanelPaidActive: {
      title: localeVpsOciStoreOperationalSystems.title,
      message: localeVpsOciStoreOperationalSystems.confirmWithoutPanelPaidActive.message,
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreOperationalSystems.confirmWithoutPanelPaidActive.agreedTerms,
      testId: 'vpsoci-operationalSystems-withoutPanelPaid-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreOperationalSystems.confirmWithoutPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-operationalSystems-withoutPanelPaid-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreOperationalSystems.confirmWithoutPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-operationalSystems-withoutPanelPaid-confirm-modal-close-button',
      },
    },
    confirmWithPanelPaidActive: {
      title: localeVpsOciStoreOperationalSystems.title,
      message: localeVpsOciStoreOperationalSystems.confirmWithPanelPaidActive.message(formattedAddonsText),
      alert: localeVpsOciStoreModalAlert.backup,
      agreedTerms: localeVpsOciStoreOperationalSystems.confirmWithPanelPaidActive.agreedTerms(operationalSystemName, formattedAddonsText),
      testId: 'vpsoci-operationalSystems-withPanelPaid-confirm-modal',
      confirmButton: {
        text: localeVpsOciStoreOperationalSystems.confirmWithPanelPaidActive.confirmButton,
        action: handleStepConfirm,
        loading: changeImageLoading,
        testId: 'vpsoci-operationalSystems-withPanelPaid-confirm-modal-confirm-button',
      },
      closeButton: {
        text: localeVpsOciStoreOperationalSystems.confirmWithPanelPaidActive.closeButton,
        action: handleBackStep,
        testId: 'vpsoci-operationalSystems-withPanelPaid-confirm-modal-close-button',
      },
    },
  }), [
    localeVpsOciStoreOperationalSystems,
    localeVpsOciStoreModalAlert,
    selectedVersionId,
    formattedAddonsText,
    operationalSystemName,
    handleCloseModal,
    modalOperationalSystemDetails,
    handleStepVersion,
    handleBackStep,
    handleStepConfirm,
    changeImageLoading,
  ]);

  if (!selectedItem || !operationalSystems || Object.keys(operationalSystems).length === 0) return null;

  return (
    <VpsStoreModal
      modalType={modalType}
      modalConfig={modalConfig}
      onClose={handleCloseModal}
    />
  );
};

export default ModalOperationalSystems;
